import type { DayAvailability } from "../types/provider-registration";

const PENDING_PROVIDER_SCHEDULES_KEY = "servix_pending_provider_schedules";

type PendingProviderScheduleEntry = {
	providerId: number;
	availability: DayAvailability[];
	createdAt: string;
};

function readPendingEntries(): PendingProviderScheduleEntry[] {
	if (typeof window === "undefined") return [];
	try {
		const raw = window.localStorage.getItem(PENDING_PROVIDER_SCHEDULES_KEY);
		if (!raw) return [];
		const parsed = JSON.parse(raw) as unknown;
		if (!Array.isArray(parsed)) return [];
		return parsed.filter((entry): entry is PendingProviderScheduleEntry => {
			if (!entry || typeof entry !== "object") return false;
			const candidate = entry as Partial<PendingProviderScheduleEntry>;
			return (
				typeof candidate.providerId === "number" &&
				candidate.providerId > 0 &&
				Array.isArray(candidate.availability)
			);
		});
	} catch {
		return [];
	}
}

function writePendingEntries(entries: PendingProviderScheduleEntry[]) {
	if (typeof window === "undefined") return;
	if (entries.length === 0) {
		window.localStorage.removeItem(PENDING_PROVIDER_SCHEDULES_KEY);
		return;
	}
	window.localStorage.setItem(
		PENDING_PROVIDER_SCHEDULES_KEY,
		JSON.stringify(entries),
	);
}

export function savePendingProviderSchedule(
	providerId: number,
	availability: DayAvailability[],
) {
	if (!Number.isFinite(providerId) || providerId <= 0) return;
	const enabledAvailability = availability.filter((slot) => slot.enabled);
	if (enabledAvailability.length === 0) return;

	const entries = readPendingEntries().filter(
		(entry) => entry.providerId !== providerId,
	);

	entries.push({
		providerId,
		availability: enabledAvailability,
		createdAt: new Date().toISOString(),
	});

	writePendingEntries(entries);
}

export function getPendingProviderSchedule(
	providerId: number,
): DayAvailability[] | null {
	if (!Number.isFinite(providerId) || providerId <= 0) return null;
	const entries = readPendingEntries();
	const found = entries.find((entry) => entry.providerId === providerId);
	if (!found) return null;
	return found.availability;
}

export function clearPendingProviderSchedule(providerId: number) {
	if (!Number.isFinite(providerId) || providerId <= 0) return;
	const entries = readPendingEntries();
	writePendingEntries(entries.filter((entry) => entry.providerId !== providerId));
}

export function pullPendingProviderSchedule(
	providerId: number,
): DayAvailability[] | null {
	const found = getPendingProviderSchedule(providerId);
	if (!found) return null;
	clearPendingProviderSchedule(providerId);
	return found;
}
